import { useEffect, useRef } from 'react';
import { gsap, ScrollTrigger } from '@/hooks/useGsapAnimations';

gsap.registerPlugin(ScrollTrigger);

const CTASection = () => {
    const sectionRef = useRef<HTMLElement>(null);

    useEffect(() => {
        const el = sectionRef.current;
        if (!el) return;

        const ctx = gsap.context(() => {
            const tl = gsap.timeline({
                scrollTrigger: {
                    trigger: el,
                    start: 'top 85%',
                    toggleActions: 'play none none none',
                },
            });

            // Card scales up slightly from 0.95
            tl.fromTo('.cta-card', { opacity: 0, scale: 0.95 }, { opacity: 1, scale: 1, duration: 0.7, ease: 'power2.out' });

            // Title fades up
            tl.fromTo('.cta-title', { opacity: 0, y: 25 }, { opacity: 1, y: 0, duration: 0.6, ease: 'power2.out' }, '-=0.3');

            // Description fades up
            tl.fromTo('.cta-description', { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.5, ease: 'power2.out' }, '-=0.2');

            // Buttons stagger in
            tl.fromTo(
                '.cta-btn',
                { opacity: 0, y: 20 },
                { opacity: 1, y: 0, duration: 0.5, ease: 'power2.out', stagger: 0.15 },
                '-=0.2'
            );
        }, el);

        return () => ctx.revert();
    }, []);

    return (
        <section ref={sectionRef} id="cta" className="relative z-10 px-4 sm:px-6 lg:px-20 py-20 md:py-30   max-w-[1200px]  mx-auto">
            {/* CTA Card */}
            <div className="cta-card relative rounded-[20px] p-8 sm:p-10 md:p-12 lg:p-16 bg-[#06050B] border-[1px] border-[#8E57FA66]/40 backdrop-blur-md shadow-2xl shadow-[#8E57FA66]/20 overflow-hidden" style={{ opacity: 0 }}>
                {/* Glow effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#8E57FA]/10 to-transparent opacity-50" />

                {/* Decorative elements */}
                <div className="absolute top-0 left-0 w-full h-full opacity-20 pointer-events-none">
                    <div className="absolute top-10 left-10 w-32 h-32 bg-[#8E57FA] rounded-full blur-[100px]" />
                    <div className="absolute bottom-10 right-10 w-32 h-32 bg-[#8E57FA] rounded-full blur-[100px]" />
                </div>

                {/* Content */}
                <div className="relative z-10  text-center space-y-6 sm:space-y-8">
                    {/* Title */}
                    <h2
                        className="cta-title text-white max-w-2xl mx-auto"
                        style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontWeight: 600,
                            fontStyle: "SemiBold",
                            fontSize: "clamp(24px, 5vw, 48px)",
                            lineHeight: "clamp(32px, 6vw, 55px)",
                            letterSpacing: "clamp(0px, 0.2vw, 1px)",
                            opacity: 0,
                        }}
                    >
                        Ready to Transform Your Learning?
                    </h2>

                    {/* Description */}
                    <p
                        className="cta-description text-[#DCCBFD] max-w-2xl mx-auto"
                        style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontWeight: 400,
                            fontSize: "clamp(14px, 2.5vw, 16px)",
                            lineHeight: "clamp(22px, 4vw, 28px)",
                            letterSpacing: "0.3px",
                            opacity: 0,
                        }}
                    >
                        Join millions of students who are already learning smarter with Eddy AI. Start your journey today.
                    </p>

                    {/* App Store Buttons */}
                    <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-5 pt-0">
                        <a
                            href="https://apps.apple.com/app/eddy-ai/id6758787935"
                            className="cta-btn hover:scale-105 transition-all active:scale-95 drop-shadow-xl"
                            style={{ opacity: 0 }}
                        >
                            <img
                                src="/heroSection/AppleStoreLabel.svg"
                                alt="App Store"
                                className="h-[40px] sm:h-[44px] md:h-[48px] w-auto"
                            />
                        </a>
                        <a
                            href="https://play.google.com/store/apps/details?id=com.eddyai.scanner"
                            className="cta-btn hover:scale-105 transition-all active:scale-95 drop-shadow-xl"
                            style={{ opacity: 0 }}
                        >
                            <img
                                src="/heroSection/PlaystoreLabel.svg"
                                alt="Play Store"
                                className="h-[40px] sm:h-[44px] md:h-[48px] w-auto"
                            />
                        </a>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default CTASection;
